// Generated by Claude Code
import { useCallback, useMemo, useState } from 'react'
import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating } from '@floating-ui/react'
import type { ElementProps, Middleware, Placement, Strategy, UseDismissProps, UseFloatingReturn } from '@floating-ui/react'
import tokens from '#ynt-design-system/tokens.module.scss'
import { useEscapeKey } from '#hooks/use-escape-key.ts'

/** Side of the trigger the element is asked for. It flips when that side has no room. */
export type FloatingSide = 'top' | 'bottom' | 'left' | 'right'

/** Where the element sits along the trigger's other axis. */
export type FloatingAlign = 'start' | 'center' | 'end'

export const DEFAULT_FLOATING_OFFSET = parseFloat(tokens['size-4'])
export const DEFAULT_FLOATING_VIEWPORT_MARGIN = parseFloat(tokens['size-8'])

// A trigger at the viewport edge still gets a panel tall enough for a few rows.
const MIN_AVAILABLE_HEIGHT = 160

export interface UseFloatingElementOptions {
  placement?: FloatingSide
  align?: FloatingAlign
  /** Distance from the trigger, in px. */
  offset?: number
  /** Keep-on-screen inset from the viewport edges, in px. */
  viewportMargin?: number
  strategy?: Strategy
  /** Give the element the trigger's width, for a list that opens under its field. */
  matchTriggerWidth?: boolean
  disabled?: boolean
  /** Controlled open state. Omit to let the hook own it. */
  open?: boolean
  /** `event` is the interaction behind the change, when there is one. */
  onOpenChange?: (open: boolean, event?: Event) => void
  /** Registers the element in the surrounding `FloatingTree`, for nested elements. */
  nodeId?: string
}

export interface UseFloatingElementResult
  extends Pick<UseFloatingReturn, 'refs' | 'floatingStyles' | 'context'> {
  open: boolean
  setOpen: (open: boolean, event?: Event) => void
  /** The side the element landed on once flipping is accounted for. */
  side: FloatingSide
}

export function toPlacement(side: FloatingSide, align: FloatingAlign): Placement {
  return align === 'center' ? side : `${side}-${align}`
}

/**
 * Positions an element against its trigger and owns its open state, or follows `open` when
 * controlled. Interactions build on `context`; see `useTooltip`, `useDropdown` and `useSubmenu`.
 */
export function useFloatingElement({
  placement = 'bottom',
  align = 'center',
  offset: distance = DEFAULT_FLOATING_OFFSET,
  viewportMargin = DEFAULT_FLOATING_VIEWPORT_MARGIN,
  strategy = 'fixed',
  matchTriggerWidth = false,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  nodeId,
}: UseFloatingElementOptions = {}): UseFloatingElementResult {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = (isControlled ? controlledOpen : uncontrolledOpen) && !disabled

  const setOpen = useCallback(
    (next: boolean, event?: Event) => {
      if (!isControlled) setUncontrolledOpen(next)
      if (event) onOpenChange?.(next, event)
      else onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  const middleware = useMemo<Middleware[]>(
    () => [
      offset(distance),
      flip({ padding: viewportMargin }),
      shift({ padding: viewportMargin }),
      // After flip, so the height is measured on the side the element landed on. Styles cap
      // themselves with `--floating-available-height`.
      size({
        padding: viewportMargin,
        apply({ availableHeight, rects, elements }) {
          const style = elements.floating.style
          if (Number.isFinite(availableHeight)) {
            style.setProperty('--floating-available-height', `${Math.max(availableHeight, MIN_AVAILABLE_HEIGHT)}px`)
          }
          if (matchTriggerWidth) style.setProperty('--floating-trigger-width', `${rects.reference.width}px`)
        },
      }),
    ],
    [distance, viewportMargin, matchTriggerWidth],
  )

  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    nodeId,
    placement: toPlacement(placement, align),
    strategy,
    whileElementsMounted: autoUpdate,
    middleware,
  })

  return {
    open,
    setOpen,
    context: floating.context,
    refs: floating.refs,
    floatingStyles: floating.floatingStyles,
    side: floating.placement.split('-')[0] as FloatingSide,
  }
}

/**
 * Closes the element on an outside press and on Escape, which goes through the `useEscapeKey`
 * stack so one press closes only the top layer. Pass the result to `useInteractions`.
 */
export function useFloatingDismiss(
  floating: Pick<UseFloatingElementResult, 'context' | 'open' | 'setOpen'>,
  options: Omit<UseDismissProps, 'escapeKey'> = {},
): ElementProps {
  const { setOpen } = floating
  useEscapeKey(() => setOpen(false), floating.open && (options.enabled ?? true))
  return useDismiss(floating.context, { ...options, escapeKey: false })
}

export default useFloatingElement
